// File: src/App.js
import React, { useEffect, useState, useMemo } from "react";
import "./App.css";
import API from "./config";
import * as XLSX from "xlsx";


// Helper to fetch JSON
const fetchJSON = (url, opts = {}) =>
  fetch(url, opts).then(async (r) => {
    const txt = await r.text();
    try {
      return { ok: r.ok, status: r.status, data: JSON.parse(txt) };
    } catch {
      return { ok: r.ok, status: r.status, data: txt };
    }
  });

/* ----------------------------- DATA HELPERS ----------------------------- */

// Robustly extract State Name (Student State)
const getSafeState = (row) => {
  const val = row.STATE || row.State || row["State/UT Name"] || row["State Name"] || "UNKNOWN";
  return String(val).trim();
};

// Robustly extract University State based on headers
const getSafeUniversityState = (row) => {
  if (row["UNIVERSITY_STATE"] !== undefined) return String(row["UNIVERSITY_STATE"]).trim();
  if (row.University_State) return String(row.University_State).trim();
  if (row["University State"]) return String(row["University State"]).trim();

  const key = Object.keys(row).find(k => 
    k.toUpperCase() === "UNIVERSITY_STATE" || 
    k.toUpperCase() === "UNIVERSITY STATE"
  );
  
  const val = key ? row[key] : "UNKNOWN";
  return String(val || "UNKNOWN").trim();
};

// Robustly extract Financial Year
const getSafeYear = (row) => {
  const val = row.FINANCE_YEAR || row["Financial Year"] || row.Year || row.YEAR || "";
  return String(val).trim();
};

// Robustly extract Month/Year (e.g., "Nov-2022")
const getSafeMonthYear = (row) => {
  const val = row.MONTH_YEAR || row["Month"] || row["Month-Year"] || "";
  return String(val).trim();
};

// Robustly extract Gender
const getSafeGender = (row) => {
  const val = row.GENDER || row.Gender || row.Sex || "";
  return String(val).toLowerCase().trim();
};

// Robustly extract Amount
const getSafeAmount = (row, amtKey) => {
  if (!amtKey) return 0;
  let val = row[amtKey];
  if (val === undefined || val === null || val === "") return 0;
  val = String(val).replace(/,/g, ""); 
  return parseFloat(val) || 0;
};

// Robust Student ID Getter
const getSafeStudentId = (row) => {
  return String(row.STUDENT_ID || row.Student_Id || row.ID || "UNKNOWN").trim();
};

// SMART YEAR PARSER
const getYearStart = (fyString) => {
  if (!fyString || fyString === "ALL") return 0;
  const match = String(fyString).match(/(\d{4})/); 
  return match ? parseInt(match[1]) : 0;
};

// HELPER: Sort "MMM-YYYY" or "Month-YYYY" chronologically
const parseMonthYear = (dateStr) => {
  if (!dateStr) return 0;
  const parts = dateStr.split(/[- ]/);
  if (parts.length < 2) return 0;

  const monthMap = {
    jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
    jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11
  };
  
  const mStr = parts[0].toLowerCase().substring(0, 3);
  const year = parseInt(parts[1]);
  const month = monthMap[mStr];

  if (isNaN(year) || month === undefined) return 0;
  
  return new Date(year, month, 1).getTime();
};



/* --------------------------- RESULTS TABLE ------------------------------ */
function ResultsTable({ rows, summaryText }) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  useEffect(() => setPage(1), [rows, search]);

  const filtered = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter((r) =>
      Object.values(r).some((val) => String(val).toLowerCase().includes(s))
    );
  }, [rows, search]);

  const total = filtered.length;
  const isLargeData = total > 500;

  const start = isLargeData ? (page - 1) * pageSize : 0;
  const end = isLargeData ? start + pageSize : total;
  const visibleRows = filtered.slice(start, end);
  const totalPages = isLargeData ? Math.ceil(total / pageSize) : 1;

  const isTotalRow = (row) => {
    const vals = Object.values(row);
    if (vals[0] && String(vals[0]).toUpperCase().includes("TOTAL")) return true;
    if (vals[1] && String(vals[1]).toUpperCase().includes("TOTAL")) return true;
    if (vals[2] && String(vals[2]).toUpperCase().includes("TOTAL")) return true;
    return false;
  };

  if (!rows || rows.length === 0) {
    return (
      <div className="card results-card">
        <div className="p-20 muted">No data generated yet.</div>
      </div>
    );
  }

  return (
    <div className="card results-card only-results-expand">
      <div className="results-header">
        <div>
          <h3>Results ({total})</h3>
          {summaryText && (
            <div className="muted" style={{ fontSize: 13, marginTop: 4, color: '#138808', fontWeight: 600 }}>
              {summaryText}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {isLargeData && (
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
              <option value={500}>500 per page</option>
              <option value={1000}>1000 per page</option>
            </select>
          )}
         <input
          className="gov-search-input"
          placeholder="Quick search results..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        </div>
      </div>

      <div className="table-wrap only-results-expand">
        <table className="results-table">
          <thead>
            <tr>
              {visibleRows[0] &&
                Object.keys(visibleRows[0]).map((k) => <th key={k}>{k}</th>)}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r, i) => {
              const isBold = isTotalRow(r);
              return (
                <tr
                  key={i}
                  style={isBold ? { fontWeight: "bold", backgroundColor: "#f0f8ff" } : {}}
                >
                  {Object.keys(r).map((k) => (
                    <td key={k}>{r[k]}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="pager" style={{ justifyContent: 'center', padding: '10px', background: '#f8f9fa', borderTop: '1px solid #eee' }}>
        <span style={{ fontWeight: 600, margin: '0 10px' }}>
          Page {page} of {totalPages} ({start + 1}-{Math.min(end, total)} of {total} rows)
        </span>
        <div className="pager-controls">
           <button disabled={page === 1} onClick={() => setPage(1)}>«</button>
           <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
           <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
           <button disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
        </div>
      </div>
    </div>
  );
}

/* --------------------------- REPORTS PAGE ------------------------------- */
export default function ReportsPage({ token }) {
  const [lists, setLists] = useState({});
  const [active, setActive] = useState("state-wise");
  const [rows, setRows] = useState([]);

  // Loaders
  const [loading, setLoading] = useState(false);
  const [loadingPercent, setLoadingPercent] = useState(0);
  
  const [isExporting, setIsExporting] = useState(false);
  const [exportPercent, setExportPercent] = useState(0);

  const [listLoading, setListLoading] = useState(false);
  const [listLoadingPercent, setListLoadingPercent] = useState(0);

  const [summaryText, setSummaryText] = useState("");

  const [form, setForm] = useState({
    from_date: "",
    to_date: "",
    from_fy: "ALL",
    to_fy: "ALL",
    state: "ALL",
    month_year: "",
    q: "",
  });

  // Safe accessor for backend year list
  const yearsList = lists.yearRange || []; 

  // --- ANIMATIONS ---
  // Blue Bar Animation (Generate)
  useEffect(() => {
    let timer;
    if (loading) {
      setLoadingPercent(0);
      timer = setInterval(() => setLoadingPercent((p) => (p >= 90 ? p : p + 5)), 200);
    } else {
      setLoadingPercent(100);
      setTimeout(() => setLoadingPercent(0), 300);
    }
    return () => clearInterval(timer);
  }, [loading]);

  // Green Bar Animation (Lists)
  useEffect(() => {
    let timer;
    if (listLoading) {
      setListLoadingPercent(0);
      timer = setInterval(() => setListLoadingPercent((p) => (p >= 95 ? p : p + 10)), 100);
    } else {
      setListLoadingPercent(100);
      setTimeout(() => setListLoadingPercent(0), 500);
    }
    return () => clearInterval(timer);
  }, [listLoading]);

  // --- DATA FETCHING ---
  useEffect(() => {
    async function loadLists() {
      setListLoading(true);
      try {
        const res = await fetchJSON(`${API}/api/pms/lists`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setLists(res.data || {});
      } catch (e) {
        console.error(e);
      } finally {
        setListLoading(false);
      }
    }
    if (token) loadLists();
  }, [token]);

  const detectAmountKey = (data) => {
    if (!data || data.length === 0) return null;
    const sample = data[0];
    const keys = Object.keys(sample);
    for (let k of keys) {
      const clean = k.toLowerCase().replace(/\s+/g, "");
      // Matches AMT_PAID from your headers
      if (clean.includes("amt") || clean.includes("amount") || clean.includes("paid")) {
        return k;
      }
    }
    return null;
  };

  const fetchFiltered = async (extraFilters = {}) => {
    setLoading(true);
    try {
      const res = await fetchJSON(`${API}/api/pms/filter`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(extraFilters),
      });
      
      if (!res.ok) {
        alert(res.data?.error || "Filter failed");
        setLoading(false);
        return [];
      }
      setLoading(false);
      return res.data.rows || [];
    } catch (e) {
      console.error(e);
      setLoading(false);
      return [];
    }
  };

  const isInFyRange = (row) => {
    const rowFy = getSafeYear(row);
    if (!rowFy) return false;

    const rStart = getYearStart(rowFy);
    const fStart = getYearStart(form.from_fy);
    const tStart = getYearStart(form.to_fy);

    if (fStart > 0 && rStart < fStart) return false;
    if (tStart > 0 && rStart > tStart) return false;

    return true;
  };

  // --- REPORT GENERATORS ---

  const genStateWise = async () => {
    const data = await fetchFiltered({ ...form });
    const amtKey = detectAmountKey(data);
    const globalMenIds = new Set();
    const globalWomenIds = new Set();
    let totalAmt = 0;

    const groups = {};

    data.forEach((r) => {
      if (!isInFyRange(r)) return;

      const rawAmt = getSafeAmount(r, amtKey);
      if (rawAmt === 0) return; // Skip 0 amounts (Refunds)

      const amtCrore = rawAmt / 10000000;
      const state = getSafeState(r);
      const gender = getSafeGender(r);
      const studentId = getSafeStudentId(r);

      if (!groups[state]) {
        groups[state] = { 
            state, 
            menIds: new Set(), 
            womenIds: new Set(), 
            amtMale: 0, 
            amtFemale: 0, 
            amtTotal: 0 
        };
      }

      if (gender === "female" || gender === "f" || gender.includes("woman")) {
        groups[state].womenIds.add(studentId);
        globalWomenIds.add(studentId);
        groups[state].amtFemale += amtCrore;
      } else {
        groups[state].menIds.add(studentId);
        globalMenIds.add(studentId);
        groups[state].amtMale += amtCrore;
      }

      groups[state].amtTotal += amtCrore;
      totalAmt += amtCrore;
    });

    // ALPHABETICAL SORT BY STATE
    const sortedGroups = Object.values(groups).sort((a, b) => 
      a.state.localeCompare(b.state)
    );

    const out = sortedGroups.map((g, i) => ({
      "S.No.": i + 1,
      "State/UT Name": g.state,
      "Scholars (Male)": g.menIds.size,
      "Scholars (Female)": g.womenIds.size,
      "Scholars (Total)": g.menIds.size + g.womenIds.size,
      "Amount (Male) (Cr.)": Number(g.amtMale.toFixed(2)),
      "Amount (Female) (Cr.)": Number(g.amtFemale.toFixed(2)),
      "Amount (Total) (Cr.)": Number(g.amtTotal.toFixed(2)),
    }));

    out.push({
      "S.No.": "", "State/UT Name": "TOTAL (All States)",
      "Scholars (Male)": globalMenIds.size,
      "Scholars (Female)": globalWomenIds.size,
      "Scholars (Total)": globalMenIds.size + globalWomenIds.size,
      "Amount (Male) (Cr.)": Number(out.reduce((s, r) => s + r["Amount (Male) (Cr.)"], 0).toFixed(2)),
      "Amount (Female) (Cr.)": Number(out.reduce((s, r) => s + r["Amount (Female) (Cr.)"], 0).toFixed(2)),
      "Amount (Total) (Cr.)": Number(totalAmt.toFixed(2)),
    });

    setSummaryText(`Total Unique Scholars: ${globalMenIds.size + globalWomenIds.size}`);
    setRows(out);
  };

  const genUniversityWise = async () => {
    const filterPayload = { ...form };
    if (filterPayload.state !== "ALL") {
        filterPayload.state = "ALL"; 
    }

    const data = await fetchFiltered(filterPayload);
    const amtKey = detectAmountKey(data);
    const groups = {};
    const globalIds = new Set();
    let totalAmt = 0;

    data.forEach((r) => {
      if (!isInFyRange(r)) return;

      const rawAmt = getSafeAmount(r, amtKey);
      if (rawAmt === 0) return; // Skip 0 amounts (Refunds)

      const uniState = getSafeUniversityState(r);

      if (form.state !== "ALL" && uniState.toLowerCase() !== form.state.toLowerCase()) {
          return;
      }

      const uni = r.UNIVERSITY_NAME || r.University || r["University/Institute Name"] || "UNKNOWN";
      const studentId = getSafeStudentId(r);
      const amtCrore = rawAmt / 10000000;

      if (!groups[uni]) {
        groups[uni] = { uni, uniState, studentIds: new Set(), amt: 0 };
      } else if (groups[uni].uniState === "UNKNOWN" && uniState !== "UNKNOWN") {
        groups[uni].uniState = uniState;
      }
      
      groups[uni].studentIds.add(studentId);
      globalIds.add(studentId);
      groups[uni].amt += amtCrore;
      totalAmt += amtCrore;
    });

    // ALPHABETICAL SORT BY UNIVERSITY STATE, THEN UNIVERSITY NAME
    const sortedGroups = Object.values(groups).sort((a, b) => {
      const stateCmp = a.uniState.localeCompare(b.uniState);
      if (stateCmp !== 0) return stateCmp;
      return a.uni.localeCompare(b.uni);
    });

    const out = sortedGroups.map((g, i) => ({
      "S.No.": i + 1, 
      "University State": g.uniState, 
      "University/Institute Name": g.uni,
      "Scholars": g.studentIds.size,
      "Amount (Cr.)": Number(g.amt.toFixed(2)),
    }));

    out.push({
      "S.No.": "", 
      "University State": "TOTAL",
      "University/Institute Name": "",
      "Scholars": globalIds.size,
      "Amount (Cr.)": Number(totalAmt.toFixed(2)),
    });

    setSummaryText(`Total Unique Scholars: ${globalIds.size}`);
    setRows(out);
  };

  const genStateYearWise = async () => {
    const data = await fetchFiltered({ ...form });
    const yearsSet = new Set();
    const amtKey = detectAmountKey(data);
    
    const states = {}; 
    const yearGrandTotals = {};

    data.forEach((r) => {
      if (isInFyRange(r)) yearsSet.add(getSafeYear(r));
    });
    const years = Array.from(yearsSet).sort();
    years.forEach(y => yearGrandTotals[y] = { ids: new Set(), amount: 0 });

    data.forEach((r) => {
      if (!isInFyRange(r)) return;
      const rawAmt = getSafeAmount(r, amtKey);
      if (rawAmt === 0) return; // Skip 0 amounts (Refunds)

      const s = getSafeState(r);
      const fy = getSafeYear(r);
      const studentId = getSafeStudentId(r);
      const amtCrore = rawAmt / 10000000;

      if (!states[s]) states[s] = {};
      if (!states[s][fy]) states[s][fy] = { ids: new Set(), amount: 0 };
      
      states[s][fy].ids.add(studentId); 
      states[s][fy].amount += amtCrore;

      if(yearGrandTotals[fy]) {
          yearGrandTotals[fy].ids.add(studentId);
          yearGrandTotals[fy].amount += amtCrore;
      }
    });

    // ALPHABETICAL SORT BY STATE KEY
    const sortedStateKeys = Object.keys(states).sort((a, b) => a.localeCompare(b));

    const out = sortedStateKeys.map((st) => {
      const row = { "State-UT": st };
      const stateTotalUniqueSet = new Set(); 
      let totalAmt = 0;

      years.forEach((y) => {
        const val = states[st][y] || { ids: new Set(), amount: 0 };
        row[`${y} (No.)`] = val.ids.size; 
        row[`${y} (Cr.)`] = Number(val.amount.toFixed(2));
        val.ids.forEach(id => stateTotalUniqueSet.add(id));
        totalAmt += val.amount;
      });

      row["TOTAL Scholars"] = stateTotalUniqueSet.size; 
      row["TOTAL Amount"] = Number(totalAmt.toFixed(2));
      return row;
    });

    const totalRow = { "State-UT": "GRAND TOTAL" };
    let grandUniqueSet = new Set();
    let grandAmt = 0;

    years.forEach(y => {
       const v = yearGrandTotals[y];
       totalRow[`${y} (No.)`] = v.ids.size;
       totalRow[`${y} (Cr.)`] = Number(v.amount.toFixed(2));
       v.ids.forEach(id => grandUniqueSet.add(id));
       grandAmt += v.amount;
    });

    totalRow["TOTAL Scholars"] = grandUniqueSet.size;
    totalRow["TOTAL Amount"] = Number(grandAmt.toFixed(2));

    out.push(totalRow);

    setRows(out);
    setSummaryText("Note: All counts represent unique students. 'Grand Total' de-duplicates students across states if applicable.");
  };

  const genBeneficiaryList = async () => {
    const data = await fetchFiltered({ ...form });
    const amtKey = detectAmountKey(data);
    
    const students = {}; 
    let totalAmt = 0;

    data.forEach((r) => {
      if (!isInFyRange(r)) return;
      const rawAmt = getSafeAmount(r, amtKey);
      if (rawAmt === 0) return; // Skip 0 amounts (Refunds)

      const sid = getSafeStudentId(r);
      const amtCrore = rawAmt / 10000000;

      if (!students[sid]) {
        students[sid] = {
          id: sid,
          name: r.STUDENT_NAME || r.Student_Name || "",
          state: getSafeState(r),
          uni: r.UNIVERSITY_NAME || "UNKNOWN",
          fy: getSafeYear(r), 
          count: 0,
          totalAmt: 0
        };
      }

      students[sid].count += 1;
      students[sid].totalAmt += amtCrore;
      totalAmt += amtCrore;
    });

    // ALPHABETICAL SORT BY STUDENT NAME
    const sortedStudents = Object.values(students).sort((a, b) => a.name.localeCompare(b.name));

    const out = sortedStudents.map((s, i) => ({
        "S.No.": i + 1,
        "Student ID": s.id,
        "Student Name": s.name,
        "State": s.state,
        "University": s.uni,
        "Payments Received": s.count,
        "Total Amount (Cr.)": Number(s.totalAmt.toFixed(4))
    }));

    out.push({
        "S.No.": "", "Student ID": "TOTAL", "Student Name": "",
        "State": "", "University": "",
        "Payments Received": out.length,
        "Total Amount (Cr.)": Number(totalAmt.toFixed(4))
    });

    setSummaryText(`Unique Beneficiaries: ${Object.keys(students).length} | Total Amount: ${totalAmt.toFixed(2)} Cr.`);
    setRows(out);
  };

  const genStateYearSummary = async () => {
    const data = await fetchFiltered({ ...form });
    const amtKey = detectAmountKey(data); 

    let yearsToDisplay = yearsList.length > 0 ? yearsList : [];
    const fStart = getYearStart(form.from_fy);
    const tStart = getYearStart(form.to_fy);

    if (fStart > 0 || tStart > 0) {
      yearsToDisplay = yearsToDisplay.filter(y => {
        const yStart = getYearStart(y);
        return !(fStart > 0 && yStart < fStart) && !(tStart > 0 && yStart > tStart);
      });
    }

    const states = {}; 
    const yearGrandTotals = {};
    yearsToDisplay.forEach(y => yearGrandTotals[y] = { men: new Set(), women: new Set() });

    data.forEach((r) => {
      if (!isInFyRange(r)) return;
      const rawAmt = getSafeAmount(r, amtKey);
      if (rawAmt === 0) return; // Skip 0 amounts (Refunds)

      const st = getSafeState(r);
      const fy = getSafeYear(r); 
      const studentId = getSafeStudentId(r);
      const g = getSafeGender(r);
      
      if (!states[st]) states[st] = {};
      if (!states[st][fy]) states[st][fy] = { menIds: new Set(), womenIds: new Set() };

      if (g === "female" || g === "f" || g.includes("woman")) {
        states[st][fy].womenIds.add(studentId);
        if(yearGrandTotals[fy]) yearGrandTotals[fy].women.add(studentId);
      } else {
        states[st][fy].menIds.add(studentId);
        if(yearGrandTotals[fy]) yearGrandTotals[fy].men.add(studentId);
      }
    });

    // ALPHABETICAL SORT BY STATE KEY
    const sortedStateKeys = Object.keys(states).sort((a, b) => a.localeCompare(b));

    const out = sortedStateKeys.map((st) => {
      const row = { "STATE/UT": st };
      yearsToDisplay.forEach((y) => {
        const v = states[st][y] || { menIds: new Set(), womenIds: new Set() };
        row[`${y} Men`] = v.menIds.size;
        row[`${y} Women`] = v.womenIds.size;
      });
      return row;
    });

    const totalRow = { "STATE/UT": "TOTAL (All States)" };
    yearsToDisplay.forEach((y) => {
      const v = yearGrandTotals[y];
      if(v) {
        totalRow[`${y} Men`] = v.men.size;
        totalRow[`${y} Women`] = v.women.size;
      }
    });
    out.push(totalRow);

    const allMenIds = new Set();
    const allWomenIds = new Set();
    Object.values(yearGrandTotals).forEach(y => {
        y.men.forEach(id => allMenIds.add(id));
        y.women.forEach(id => allWomenIds.add(id));
    });

    setSummaryText(`Total Unique Men: ${allMenIds.size} | Total Unique Women: ${allWomenIds.size}`);
    setRows(out);
  };

  const genGenderTrend = async (type) => { 
    const data = await fetchFiltered({ ...form });
    const amtKey = detectAmountKey(data);
    const counts = {}; 
    
    const totalMenSet = new Set();
    const totalWomenSet = new Set();
    let totalAmtM = 0, totalAmtW = 0;

    data.forEach((r) => {
      if (!isInFyRange(r)) return;
      const rawAmt = getSafeAmount(r, amtKey);
      if (rawAmt === 0) return; // Skip 0 amounts (Refunds)

      const fy = getSafeYear(r);
      const st = getSafeState(r);
      const g = getSafeGender(r);
      const studentId = getSafeStudentId(r);
      const amtCrore = rawAmt / 10000000;

      if (!counts[fy]) counts[fy] = {};
      if (!counts[fy][st]) counts[fy][st] = { menIds: new Set(), womenIds: new Set(), amtMen: 0, amtWomen: 0 };

      if (g === "female" || g === "f" || g.includes("woman")) {
        counts[fy][st].womenIds.add(studentId);
        totalWomenSet.add(studentId);
        counts[fy][st].amtWomen += amtCrore;
        totalAmtW += amtCrore;
      } else {
        counts[fy][st].menIds.add(studentId);
        totalMenSet.add(studentId);
        counts[fy][st].amtMen += amtCrore;
        totalAmtM += amtCrore;
      }
    });

    const out = [];
    // ALPHABETICAL SORT WITHIN CHRONOLOGICAL YEARS
    Object.keys(counts).sort().forEach((fy) => {
      const sortedStates = Object.keys(counts[fy]).sort((a, b) => a.localeCompare(b));
      
      sortedStates.forEach((st) => {
        const v = counts[fy][st];
        const row = { "S.No.": out.length + 1, "Financial Year": fy, "State": st };

        if (type === 'women' || type === 'both') {
           row["Women (No.)"] = v.womenIds.size;
           row["Women (Cr.)"] = Number(v.amtWomen.toFixed(2));
        }
        if (type === 'men' || type === 'both') {
           row["Men (No.)"] = v.menIds.size;
           row["Men (Cr.)"] = Number(v.amtMen.toFixed(2));
        }
        if (type === 'both') {
           row["Total (No.)"] = v.menIds.size + v.womenIds.size;
           row["Total (Cr.)"] = Number((v.amtMen + v.amtWomen).toFixed(2));
        }
        out.push(row);
      });
    });

    const totalRow = { "S.No.": "", "Financial Year": "TOTAL", "State": "" };
    if (type === 'women' || type === 'both') {
       totalRow["Women (No.)"] = totalWomenSet.size;
       totalRow["Women (Cr.)"] = Number(totalAmtW.toFixed(2));
    }
    if (type === 'men' || type === 'both') {
       totalRow["Men (No.)"] = totalMenSet.size;
       totalRow["Men (Cr.)"] = Number(totalAmtM.toFixed(2));
    }
    if (type === 'both') {
       totalRow["Total (No.)"] = totalMenSet.size + totalWomenSet.size;
       totalRow["Total (Cr.)"] = Number((totalAmtM + totalAmtW).toFixed(2));
    }
    out.push(totalRow);

    setSummaryText(`Unique Men: ${totalMenSet.size} | Unique Women: ${totalWomenSet.size} | Total Amount: ${(totalAmtM + totalAmtW).toFixed(2)} Cr.`);
    setRows(out);
  };

  const genMonthWise = async () => {
    const data = await fetchFiltered({ ...form });
    const amtKey = detectAmountKey(data);
    const months = {}; 
    
    const uniqueBeneficiaries = new Set();
    let totalFellowshipAmount = 0;

    data.forEach((r) => {
        if (!isInFyRange(r)) return;

        const rawAmt = getSafeAmount(r, amtKey);
        if (rawAmt === 0) return; // Skip 0 amounts (Refunds)

        const dateStr = getSafeMonthYear(r);
        if (form.month_year && form.month_year.trim() !== "") {
            if (!dateStr.toLowerCase().includes(form.month_year.toLowerCase())) {
                return;
            }
        }

        const studentId = getSafeStudentId(r);
        const amtCrore = rawAmt / 10000000;

        if (!months[dateStr]) {
            months[dateStr] = { ids: new Set(), amt: 0 };
        }

        months[dateStr].ids.add(studentId);
        months[dateStr].amt += amtCrore;

        uniqueBeneficiaries.add(studentId);
        totalFellowshipAmount += amtCrore;
    });

    // RETAINED CHRONOLOGICAL SORT FOR DATES
    const sortedKeys = Object.keys(months).sort((a, b) => {
        return parseMonthYear(a) - parseMonthYear(b);
    });

    const out = sortedKeys.map((k) => ({
        "Month & Year": k,
        "No. of Beneficiary": months[k].ids.size, 
        "Fellowship Amount (Cr.)": Number(months[k].amt.toFixed(2))
    }));

    out.push({
        "Month & Year": "Total",
        "No. of Beneficiary": uniqueBeneficiaries.size, 
        "Fellowship Amount (Cr.)": Number(totalFellowshipAmount.toFixed(2))
    });

    setSummaryText(`Total Unique Beneficiaries: ${uniqueBeneficiaries.size}`);
    setRows(out);
  };
  
  const onGenerate = async () => {
    setRows([]);
    setSummaryText("");
    setLoading(true);
    
    if (active === "state-wise") await genStateWise();
    else if (active === "university-wise") await genUniversityWise();
    else if (active === "state-year-wise") await genStateYearWise();
    else if (active === "beneficiary-list") await genBeneficiaryList();
    else if (active === "state-year-summary") await genStateYearSummary();
    else if (active === "gender-trend-women") await genGenderTrend('women');
    else if (active === "gender-trend-men") await genGenderTrend('men');
    else if (active === "gender-trend-both") await genGenderTrend('both');
    else if (active === "month-wise") await genMonthWise();
    
    setLoading(false);
  };

const onExportExcel = async () => {
  if (!rows || rows.length === 0) {
    alert("No data to export");
    return;
  }

  setIsExporting(true);
  setExportPercent(0);

  // Progress animation (UX only)
  for (let i = 1; i <= 5; i++) {
    setExportPercent(i * 20);
    await new Promise((r) => setTimeout(r, 80));
  }

  // 🔥 Convert JSON rows → Worksheet
  const worksheet = XLSX.utils.json_to_sheet(rows, {
    skipHeader: false,
  });

  // 🔥 Create Workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

  // 🔥 Auto column width (important)
  const colWidths = Object.keys(rows[0]).map((key) => ({
    wch: Math.max(
      key.length,
      ...rows.map((r) => String(r[key] ?? "").length)
    ),
  }));
  worksheet["!cols"] = colWidths;

  // 🔥 Download Excel
  XLSX.writeFile(
    workbook,
    `report_${active}_${Date.now()}.xlsx`,
    { compression: true }
  );

  setExportPercent(100);
  setTimeout(() => {
    setIsExporting(false);
    setExportPercent(0);
  }, 400);
};

 return (
    <div>
      <div className="card page-card">
        {/* LIST LOADING BAR (Green) */}
        <div style={{ height: listLoadingPercent > 0 ? '4px' : '0px', background: '#e0e0e0', transition: 'height 0.2s', overflow: 'hidden', marginBottom: listLoadingPercent > 0 ? 10 : 0 }}>
            <div style={{ width: `${listLoadingPercent}%`, height: '100%', background: '#28a745', transition: 'width 0.2s ease' }} />
        </div>
        
        {listLoading && (
            <div style={{fontSize: '12px', color: '#28a745', marginBottom: 10, textAlign: 'center'}}>
                Fetching Financial Years, States & University Data...
            </div>
        )}

        {/* TABS */}
        <div className="report-tabs" style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          {[
            { id: "state-wise", label: "State Wise" },
            { id: "university-wise", label: "University Wise" },
            { id: "state-year-wise", label: "State Year Wise" },
            { id: "beneficiary-list", label: "Scholar List" }, 
            { id: "state-year-summary", label: "State/Year Summary" },
            { id: "gender-trend-women", label: "Gender Trend (Women)" },
            { id: "gender-trend-men", label: "Gender Trend (Men)" },
            { id: "gender-trend-both", label: "Gender Trend (Both)" },
            { id: "month-wise", label: "Month Wise" },
          ].map((tab) => (
            <button key={tab.id} className={`btn ${active === tab.id ? "primary" : ""}`} onClick={() => setActive(tab.id)}>
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{ marginTop: 14 }} />

  {/* FILTERS */}
        <div className="card filters-card no-stretch">
          <h3>
            {active === "state-wise" ? "State Wise" :
             active === "university-wise" ? "University Wise" :
             active === "state-year-wise" ? "State Year Wise" :
             active === "beneficiary-list" ? "Beneficiary List (Consolidated)" :
             active === "state-year-summary" ? "State/Year Summary" :
             active === "month-wise" ? "Month Wise Report" :
             "Gender Trend Reports"}
          </h3>

          <div className="filters-grid no-stretch">
            <div className="field">
              <label>From Financial Year</label>
              <select value={form.from_fy} onChange={(e) => setForm((f) => ({ ...f, from_fy: e.target.value }))}>
                <option value="ALL">ALL</option>
                {yearsList.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="field">
              <label>To Financial Year</label>
                <select value={form.to_fy} onChange={(e) => setForm((f) => ({ ...f, to_fy: e.target.value }))}>
                <option value="ALL">ALL</option>
                {yearsList.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>

            <div className="field">
              <label>
                {active === "university-wise" ? "University State" : "State"}
              </label>
              
              <select value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}>
                <option value="ALL">ALL</option>
                {(active === "university-wise" 
                    ? (lists.university_states || []) 
                    : (lists.states || [])            
                ).map((x) => (
                  <option key={x} value={x}>{x}</option>
                ))}
              </select>
            </div>

            {active === "month-wise" && (
                <div className="field">
                    <label>Filter Month-Year (e.g. Nov-2022)</label>
                    <input 
                        placeholder="Type Month-Year..." 
                        value={form.month_year} 
                        onChange={(e) => setForm((f) => ({ ...f, month_year: e.target.value }))} 
                    />
                </div>
            )}

            <div className="field">
              <label>Search (All Fields)</label>
              <input placeholder="free text search" value={form.q} onChange={(e) => setForm((f) => ({ ...f, q: e.target.value }))} />
            </div>
          </div>
          
          <div className="filters-actions">
            <button 
                className="btn primary" 
                onClick={onGenerate}
                disabled={loading || isExporting}
            >
                {loading ? "Generating..." : "Generate"}
            </button>
            {/* <button 
                className="btn outline" 
                onClick={onExportExcel}
                disabled={loading || isExporting}
            >
                {isExporting ? "Exporting..." : "Export Excel"}
            </button> */}
          </div>
          
          {loading && (
             <div className="inline-progress" style={{ marginTop: 15 }}>
                <div style={{ marginBottom: 6, fontSize: '0.9rem', color: '#1976d2' }}>
                  <strong>Generating Report... {loadingPercent}%</strong>
                </div>
                <div style={{ width: "100%", height: 8, background: "#e3f2fd", borderRadius: 6, overflow: "hidden" }}>
                  <div style={{ width: `${loadingPercent}%`, height: "100%", background: "#1976d2", transition: "width 0.2s ease" }} />
                </div>
             </div>
          )}

          {isExporting && (
             <div className="inline-progress" style={{ marginTop: 15 }}>
                <div style={{ marginBottom: 6, fontSize: '0.9rem', color: '#e65100' }}>
                  <strong>Exporting Excel... {exportPercent}%</strong>
                </div>
                <div style={{ width: "100%", height: 8, background: "#ffe0b2", borderRadius: 6, overflow: "hidden" }}>
                  <div style={{ width: `${exportPercent}%`, height: "100%", background: "#ff9800", transition: "width 0.2s ease" }} />
                </div>
             </div>
          )}
        </div>

      {rows && rows.length > 0 && (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: 16,
      marginBottom: 8,
      padding: "10px 14px",
      background: "#f8f9fa",
      border: "1px solid #e0e0e0",
      borderRadius: 6
    }}
  >
    <div style={{ fontWeight: 600 }}>
      Generated Results
    </div>

    <button
      className="btn outline"
      onClick={onExportExcel}
      disabled={loading || isExporting}
    >
      {isExporting ? "Exporting..." : "Export Excel"}
    </button>
  </div>
)}

<div style={{ marginTop: 8 }}>
  <ResultsTable rows={rows} summaryText={summaryText} />
</div>

      </div>
    </div>
  );
}